/**
 * Uni Forms — the question builder.
 *
 * The missing middle of the module.  ``UniFormsPages.jsx`` lets you
 * create a *form* (title, type, status, a couple of toggles) and
 * ``UniFormsResponsePages.jsx`` lets you read what came back — but
 * between those two there was no way to say what the form actually
 * *asks*.  A published form with no questions collects nothing, so this
 * screen is what makes the rest of the module usable.
 *
 * Pages exported:
 *   * UniFormsQuestions — questions of one form: add, edit, reorder,
 *     duplicate, delete, and manage the answer options of choice-type
 *     questions.
 *
 * Route: ``/uni-forms/forms/:formId/questions``
 * (``ROUTES.UNI_FORMS_FORM_QUESTIONS``), reached from the "Questions"
 * action on the Forms list.
 *
 * Server contract
 * ---------------
 * Everything here talks to ``/api/uni-forms/questions/`` and
 * ``/api/uni-forms/options/`` (see ``uni_forms/api_urls.py``):
 *
 *   GET    questions/?form=<uuid>&page_size=200   list, in ``order``
 *   POST   questions/                      create (``order`` auto-appends)
 *          Body may include ``option_items`` for dropdown / multiple-choice
 *          / checkbox so the list is saved with the question.
 *   PATCH  questions/<id>/                 edit (same ``option_items``)
 *   DELETE questions/<id>/                 remove
 *   GET    questions/types/                the type palette, grouped
 *   POST   questions/reorder/            {form, order[]} — drag-and-drop
 *   POST   questions/<id>/duplicate/       clone with options
 *   GET    options/?question=<uuid>        list a question's choices
 *   POST   options/  PATCH/DELETE options/<id>/
 *
 * Two deliberate choices:
 *
 * 1. The type list is *fetched*, not mirrored in JS.  ``QUESTION_TYPES``
 *    on the model is ~35 entries and grows with each Forminator-parity
 *    phase; a hard-coded copy here would go stale silently and render
 *    unknown types as raw slugs.  ``questions/types/`` returns the same
 *    grouped palette the legacy visual builder uses.
 *
 * 2. Permissions are the *form's*.  A question has no owner of its own,
 *    so every row carries ``can_edit`` / ``can_delete`` resolved from the
 *    parent form, and the server re-checks on every write.  When the user
 *    may only read the form, the page renders read-only rather than
 *    offering buttons that would 403.
 */
import React from 'react';
import { useParams } from 'react-router-dom';
import { deleteJSON, getJSON, patchJSON, postJSON } from '../../api/client.js';
import { ROUTES } from '../../config.js';
import {
  FieldLabel, fmtCount, UniActionButton, UniBool, UniCheckField,
  UniConfirmModal, UniDetailRow, UniFormError, UniModal, UniPageShell,
  UniRowActions, UniSection, UniSelectField, UniStatusPill, UniTable,
  UniTextArea, UniTextField, UniToolbar, useUniAction, useUniCreate,
  useUniDelete, useUniEdit, useUniList,
} from './UniShared.jsx';
import SortableTh from '../../components/SortableTh.jsx';
import { columnIsSortable, useTableSort } from '../../lib/tableSort.js';
import {
  CALCULATION_OPS,
  CALC_RESET_MONTHS,
  CALC_RESET_WEEKDAYS,
  calculationResetScheduleLabel,
  emptyCalculationTerms,
  formulaLabel,
  isCalculationSourceType,
  monthDayOptions,
} from './uniFormCalculations.js';

const THEME = 'forms';

const EP = {
  FORMS:     '/api/uni-forms/forms/',
  QUESTIONS: '/api/uni-forms/questions/',
  OPTIONS:   '/api/uni-forms/options/',
  TYPES:     '/api/uni-forms/questions/types/',
  REORDER:   '/api/uni-forms/questions/reorder/',
};

/* Field widths, mirrored from ``Question.WIDTH_CHOICES``.  Unlike the
   question types this is a four-entry list that hasn't changed since it
   was introduced, so it isn't worth a round-trip. */
const WIDTH_OPTIONS = [
  { value: 'full',   label: 'Full width (100%)' },
  { value: 'large',  label: 'Large (~75%)' },
  { value: 'medium', label: 'Medium (~50%)' },
  { value: 'small',  label: 'Small (~25%)' },
];

/* ``Question.validation_type`` choices.  '' is a real stored value
   ("None"), not a placeholder — the model declares it explicitly. */
const VALIDATION_OPTIONS = [
  { value: '',           label: 'No extra validation' },
  { value: 'number',     label: 'Must be a number' },
  { value: 'email',      label: 'Must be an email address' },
  { value: 'url',        label: 'Must be a URL' },
  { value: 'regex',      label: 'Must match a regular expression' },
  { value: 'min_length', label: 'Minimum length' },
  { value: 'max_length', label: 'Maximum length' },
];

/** Types that take a min/max scale, so the scale fields are worth showing. */
const SCALE_TYPES = ['linear_scale'];
/** Types that take a star-rating cap. */
const RATING_TYPES = ['rating'];
/** Types with no answer at all — pure layout / content. */
const LAYOUT_TYPES = ['section_header', 'page_break', 'html'];

/* Two empty rows so a newly picked Dropdown already shows a list to fill
   in, instead of a blank modal and a later "Options" hunt. */
function emptyOptionItems() {
  return [{ text: '' }, { text: '' }];
}

function optionItemsFromQuestion(row) {
  const existing = (row && row.options) || [];
  if (!existing.length) return emptyOptionItems();
  return existing.map((o) => ({
    id: o.id,
    text: o.text || '',
    is_other: !!o.is_other,
  }));
}

function filledOptionItems(items) {
  return (items || [])
    .map((item) => ({
      id: item.id || undefined,
      text: String(item.text || '').trim(),
      is_other: !!item.is_other,
    }))
    .filter((item) => item.text);
}

/* Short, per-type copy shown while the admin is assembling a question.
   ``hint`` sits under the type picker; ``help`` is the ``?`` panel. */
const TYPE_GUIDE = {
  short_text: {
    hint: 'One-line answer. Best for names, titles, codes, or short values.',
    help: {
      title: 'Short answer',
      body: 'The respondent types a single line. Use this when a few words are enough.',
    },
  },
  long_text: {
    hint: 'Multi-line answer for comments, notes, or longer explanations.',
    help: {
      title: 'Paragraph',
      body: 'Shows a larger text box. Use it for feedback, descriptions, or anything longer than a line.',
    },
  },
  email: {
    hint: 'Validates that the answer looks like an email address.',
    help: {
      title: 'Email',
      body: 'Use this instead of Short answer when you need a contact email. Extra "must be an email" validation is already built in.',
    },
  },
  email_confirm: {
    hint: 'Email plus a confirm-email box so the address is typed twice.',
    help: {
      title: 'Email + confirm',
      body: 'The respondent must type the same email twice. Use this on registration or contact forms where a typo would lose the reply.',
    },
  },
  phone: {
    hint: 'Phone number, with optional country-code formatting.',
    help: {
      title: 'Phone number',
      body: 'Collects a telephone number. Pair it with Required if you need to call the respondent back.',
    },
  },
  url: {
    hint: 'Must look like a website address (https://…).',
    help: {
      title: 'Website / URL',
      body: 'Use this for LinkedIn profiles, company sites, or any link you want to click later from the responses.',
    },
  },
  number: {
    hint: 'Numeric answer only. Good for quantities, scores, or amounts.',
    help: {
      title: 'Number',
      body: 'Rejects letters. Prefer this over Short answer when you will total or chart the replies.',
    },
  },
  password: {
    hint: 'Masked input. The typed characters are hidden on the form.',
    help: {
      title: 'Password',
      body: 'Use only on login / registration forms. The value is stored as an answer like any other field.',
    },
  },
  hidden: {
    hint: 'Not shown to the respondent. Captured in the background on submit.',
    help: {
      title: 'Hidden field',
      body: 'Stores a value the respondent does not type: a campaign code, IP, timestamp, or device location. Pick the background source below.',
    },
  },
  multiple_choice: {
    hint: 'Pick exactly one. Add the choices under List items below.',
    help: {
      title: 'Multiple choice',
      body: 'Shows radio buttons. The respondent picks one label from the list you write. Add every choice before you publish.',
      where: 'Write one choice per row in List items. Tick “Other” on a row if they should be able to type their own answer.',
    },
  },
  checkbox: {
    hint: 'Pick any number of choices. Add the list below.',
    help: {
      title: 'Checkboxes',
      body: 'Shows tick-boxes. The respondent can select several items. Use this when more than one answer can be true at once.',
      where: 'Write each choice as its own row under List items.',
    },
  },
  dropdown: {
    hint: 'Pick one from a compact menu. Add the list items below — those labels appear in the dropdown.',
    help: {
      title: 'Dropdown list',
      body: 'The respondent opens a menu and picks one item. This is the right type for a long list of cities, products, statuses, or names. The list will not appear on the form until you add items here.',
      where: 'Type each item on its own row under “Dropdown list items”. Order here is the order in the menu. You can still fine-tune them later with the Options button on the questions list.',
    },
  },
  rating: {
    hint: 'Star (or heart) rating. Set how many stars to show.',
    help: {
      title: 'Star rating',
      body: 'The respondent taps a number of stars. Typical caps are 3, 5 or 10.',
    },
  },
  slider: {
    hint: 'Drag a handle along a numeric range.',
    help: {
      title: 'Slider / range',
      body: 'Use for “how much” answers where a number field feels too precise.',
    },
  },
  linear_scale: {
    hint: 'A numbered scale (for example 1–5) with optional end labels.',
    help: {
      title: 'Linear scale',
      body: 'Good for satisfaction or NPS-style questions. Set the from/to numbers and the labels the ends should show.',
    },
  },
  multiple_choice_grid: {
    hint: 'A table of rows × columns. Each row gets one radio choice.',
    help: {
      title: 'Multiple choice grid',
      body: 'Use for “rate each of these on the same scale”. After saving, add row and column labels from the question’s extra settings.',
    },
  },
  checkbox_grid: {
    hint: 'A table where each row can have several ticks.',
    help: {
      title: 'Checkbox grid',
      body: 'Same layout as a multiple-choice grid, but the respondent can tick more than one cell in a row.',
    },
  },
  date: {
    hint: 'Calendar date. The respondent picks a day.',
    help: {
      title: 'Date',
      body: 'Stores a calendar day. Use this for visit dates, deadlines, or dates of birth.',
    },
  },
  time: {
    hint: 'Time of day. The respondent picks hours and minutes.',
    help: {
      title: 'Time',
      body: 'Stores a clock time. Pair with a Date question if you need both.',
    },
  },
  address: {
    hint: 'Structured address fields (line, city, and so on).',
    help: {
      title: 'Address',
      body: 'Collects a postal address in separate parts so reports can filter by city or state.',
    },
  },
  geolocation: {
    hint: 'Asks the device for a location and fills an address from it.',
    help: {
      title: 'Geolocation',
      body: 'The browser or mobile app will ask for location permission. Use Hidden Field instead if you only need a silent GPS stamp.',
    },
  },
  signature: {
    hint: 'The respondent draws a signature on screen.',
    help: {
      title: 'E-signature',
      body: 'Saved as an image with the response. Use Required if the form must not be submitted unsigned.',
    },
  },
  file_upload: {
    hint: 'The respondent attaches a file (image, PDF, document).',
    help: {
      title: 'File upload',
      body: 'Use for ID photos, receipts, or supporting documents. The file is stored with the response.',
    },
  },
  multi_file: {
    hint: 'Attach more than one file to the same question.',
    help: {
      title: 'Multi-file upload',
      body: 'Same as File upload, but several files are allowed.',
    },
  },
  consent: {
    hint: 'A tick-box with legal wording. Usually required.',
    help: {
      title: 'GDPR consent',
      body: 'Write the consent sentence in Consent wording. Tick Required so the form cannot be sent without agreement.',
    },
  },
  calculation: {
    hint: 'A value computed from other questions on this form.',
    help: {
      title: 'Calculation',
      body: 'Pick at least two questions already on this form and the operations between them. Number, rating, slider, and scale answers work best; other answers count as 0 if they are not numeric. Last Answer fields carry the previous figure. You can reset those previous figures to 0 weekly, monthly, or yearly. The respondent sees the result; they only type it if you allow editing.',
    },
  },
  last_answer: {
    hint: 'Shows this person’s last answer to a question you pick from this form.',
    help: {
      title: 'Last Answer',
      body: 'Give this field its own name (for example “Last city”). Then pick which existing question to replay. For a signed-in respondent this field displays what they submitted for that question last time. It is filled in automatically unless you allow them to edit it.',
    },
  },
  section_header: {
    hint: 'A heading, not an answer. Splits a long form into parts.',
    help: {
      title: 'Section header',
      body: 'Use the question text as the heading. Help text under it becomes the section intro. Nothing is submitted for this row.',
    },
  },
  page_break: {
    hint: 'Starts a new step on a multi-page form.',
    help: {
      title: 'Page break',
      body: 'Questions after this row appear on the next step. The heading is only shown to you in the builder.',
    },
  },
  html: {
    hint: 'A notice, image, or formatted block. Not an answer.',
    help: {
      title: 'HTML block',
      body: 'Paste HTML for a banner, instructions, or an embedded image. Respondents cannot type into this.',
    },
  },
  respondent_identity: {
    hint: 'Captures who is filling the form (signed-in profile).',
    help: {
      title: 'Respondent identity',
      body: 'Shows the signed-in person’s details. Prefer this over asking them to re-type their name.',
    },
  },
};

const DEFAULT_TYPE_GUIDE = {
  hint: 'This is the control the respondent will use to answer.',
  help: {
    title: 'Question type',
    body: 'Pick the input that matches the answer you need. Dropdown, multiple choice, and checkboxes all need a list of items — that list appears as soon as you pick one of those types.',
  },
};

function typeGuide(questionType) {
  return TYPE_GUIDE[questionType] || DEFAULT_TYPE_GUIDE;
}

const CALC_OPS = CALCULATION_OPS;


/**
 * Builder for an unbounded left-to-right calculation chain.
 *
 * First row is just a source question; every extra row is
 * operator + question.  There is no maximum — "Add another" appends.
 */
function CalculationTermsEditor({ draft, set, questions, selfId }) {
  const terms = Array.isArray(draft.calculation_terms) && draft.calculation_terms.length
    ? draft.calculation_terms
    : emptyCalculationTerms();

  const eligible = (questions || []).filter((q) => {
    if (selfId && String(q.id) === String(selfId)) return false;
    const referenced = terms.some((t) => String(t.question_id) === String(q.id));
    return referenced || isCalculationSourceType(q.question_type);
  });

  const options = [
    { value: '', label: 'Choose a question…' },
    ...eligible.map((q) => ({
      value: String(q.id),
      label: q.text
        ? (q.question_type_display ? `${q.text} (${q.question_type_display})` : q.text)
        : (q.question_type_display || 'Untitled'),
    })),
  ];
  terms.forEach((t) => {
    const selected = t && t.question_id ? String(t.question_id) : '';
    if (selected && !options.some((o) => o.value === selected)) {
      options.push({ value: selected, label: 'Previously selected question' });
    }
  });

  const update = (index, patch) => {
    const next = terms.map((t, i) => (i === index ? { ...t, ...patch } : t));
    set('calculation_terms', next);
  };

  const add = () => {
    set('calculation_terms', [...terms, { op: '+', question_id: '' }]);
  };

  const remove = (index) => {
    if (terms.length <= 2) return;
    let next = terms.filter((_, i) => i !== index);
    if (next.length && next[0].op) {
      next = [{ question_id: next[0].question_id }, ...next.slice(1)];
    }
    set('calculation_terms', next);
  };

  return (
    <div className="uni-calc-builder">
      <div className="ty-field">
        <label>Calculate from</label>
        <span className="uni-field__hint">
          Pick questions already on this form. Number, rating, slider, and
          scale answers work best; other answers count as 0 if they are not
          numeric. Operations: add, subtract, multiply, divide.
        </span>
      </div>
      {eligible.length < 2 && (
        <div className="uni-note uni-note--info">
          <span aria-hidden="true">💡</span>
          <span>
            Add at least two other questions first, then come back and
            pick them here.
          </span>
        </div>
      )}
      {terms.map((term, index) => (
        <div className="uni-calc-term" key={`calc-term-${index}`}>
          {index === 0 ? (
            <span className="uni-calc-term__op uni-calc-term__op--start">Start with</span>
          ) : (
            <select
              className="ty-select uni-calc-term__op"
              aria-label={`Operation ${index}`}
              value={term.op || '+'}
              onChange={(e) => update(index, { op: e.target.value })}
            >
              {CALC_OPS.map((op) => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </select>
          )}
          <select
            className="ty-select uni-calc-term__question"
            aria-label={index === 0 ? 'First question' : `Question ${index + 1}`}
            value={term.question_id || ''}
            onChange={(e) => update(index, { question_id: e.target.value })}
          >
            {options.map((o) => (
              <option key={`${index}-${o.value || 'blank'}`} value={o.value}>{o.label}</option>
            ))}
          </select>
          {terms.length > 2 && (
            <button
              type="button"
              className="ty-btn ty-btn--ghost ty-btn--sm"
              onClick={() => remove(index)}
              title="Remove this question from the calculation"
            >
              Remove
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        className="ty-btn ty-btn--secondary ty-btn--sm"
        onClick={add}
      >
        + Add another question
      </button>
      {formulaLabel(terms, questions) && terms.some((t) => t.question_id) && (
        <div className="uni-calc-preview">
          Preview: {formulaLabel(terms, questions)}
        </div>
      )}
      <UniCheckField
        label="Allow respondent to edit this answer"
        checked={!!draft.calculation_editable}
        onChange={(v) => set('calculation_editable', v)}
        hint={draft.calculation_editable
          ? 'The value still updates when the other answers change, but the respondent can then type a different number.'
          : 'The field stays greyed out. Respondents can see the result but cannot change it.'}
      />
      <CalculationResetFields draft={draft} set={set} />
    </div>
  );
}

const RESET_PERIOD_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

function CalculationResetFields({ draft, set }) {
  const enabled = !!draft.calculation_reset_enabled;
  const period = draft.calculation_reset_period || 'weekly';
  const weekday = String(draft.calculation_reset_weekday ?? 0);
  const day = String(draft.calculation_reset_day || 1);
  const month = String(draft.calculation_reset_month || 1);

  const enable = (on) => {
    set('calculation_reset_enabled', on);
    if (on && !draft.calculation_reset_period) {
      set('calculation_reset_period', 'weekly');
    }
    if (draft.calculation_reset_weekday === '' || draft.calculation_reset_weekday == null) {
      set('calculation_reset_weekday', 0);
    }
    if (!draft.calculation_reset_day) set('calculation_reset_day', 1);
    if (!draft.calculation_reset_month) set('calculation_reset_month', 1);
  };

  return (
    <div className="uni-calc-reset">
      <UniCheckField
        label="Reset this calculation"
        checked={enabled}
        onChange={enable}
        hint="Turn this on when the calculation should stop carrying previous figures and go back to 0 on a schedule. Numbers entered on the current submission still count."
        help={{
          title: 'Reset this calculation',
          body: 'Use this for a running total built from a Last Answer (last week’s calls, last month’s sales, and so on). After the reset day, those previous figures count as 0. The Last Answer field still shows the earlier number. A second submission on the same day after the reset still adds to the new period.',
        }}
      />
      {enabled && (
        <>
          <UniSelectField
            label="Reset every"
            required
            value={period}
            onChange={(v) => set('calculation_reset_period', v)}
            options={RESET_PERIOD_OPTIONS}
            hint="When the carried-forward figure goes back to 0."
            help={{
              title: 'Reset every',
              body: 'Weekly resets on a weekday. Monthly resets on a day of the month. Yearly resets on a month and day.',
            }}
          />
          {period === 'weekly' && (
            <UniSelectField
              label="Reset on"
              required
              value={weekday}
              onChange={(v) => set('calculation_reset_weekday', Number(v))}
              options={CALC_RESET_WEEKDAYS}
              hint="Previous figures count as 0 from this weekday. Sunday is the default."
              help={{
                title: 'Weekly reset day',
                body: 'The calculation starts a new week on this day. A submission on that day does not include figures from before it.',
              }}
            />
          )}
          {period === 'monthly' && (
            <UniSelectField
              label="Reset on day"
              required
              value={day}
              onChange={(v) => set('calculation_reset_day', Number(v))}
              options={monthDayOptions()}
              hint="Previous figures count as 0 from this day of the month. The 1st is the default. Shorter months use their last day."
              help={{
                title: 'Monthly reset day',
                body: 'Pick 1 for the first of the month. If you pick 31, February resets on the 28th or 29th.',
              }}
            />
          )}
          {period === 'yearly' && (
            <div className="uni-form-row">
              <UniSelectField
                label="Reset month"
                required
                value={month}
                onChange={(v) => set('calculation_reset_month', Number(v))}
                options={CALC_RESET_MONTHS}
                hint="January is the default."
                help={{
                  title: 'Yearly reset month',
                  body: 'The month when the yearly total goes back to 0. January 1st is the default.',
                }}
              />
              <UniSelectField
                label="Reset day"
                required
                value={day}
                onChange={(v) => set('calculation_reset_day', Number(v))}
                options={monthDayOptions()}
                hint="The 1st is the default."
                help={{
                  title: 'Yearly reset day',
                  body: 'Combined with the month. 1 January resets the calculation at the start of the calendar year.',
                }}
              />
            </div>
          )}
          {calculationResetScheduleLabel({
            calculation_reset_enabled: true,
            calculation_reset_period: period,
            calculation_reset_weekday: weekday,
            calculation_reset_day: day,
            calculation_reset_month: month,
          }) && (
            <div className="uni-calc-preview">
              {calculationResetScheduleLabel({
                calculation_reset_enabled: true,
                calculation_reset_period: period,
                calculation_reset_weekday: weekday,
                calculation_reset_day: day,
                calculation_reset_month: month,
              })}
              . Previous figures count as 0 from that day.
            </div>
          )}
        </>
      )}
    </div>
  );
}

const LAST_ANSWER_UNSUPPORTED_SOURCE_TYPES = [
  'section_header', 'page_break', 'html',
  'respondent_identity',
  'password', 'password_confirm',
  'stripe', 'paypal',
  'last_answer',
  'hidden',
];

function LastAnswerSourceEditor({ draft, set, questions, selfId }) {
  const eligible = (questions || []).filter((q) => {
    if (selfId && String(q.id) === String(selfId)) return false;
    return !LAST_ANSWER_UNSUPPORTED_SOURCE_TYPES.includes(q.question_type);
  });
  const selected = draft.last_answer_source || '';
  const options = [
    { value: '', label: 'Choose a question…' },
    ...eligible.map((q) => ({
      value: String(q.id),
      label: q.text
        ? (q.question_type_display ? `${q.text} (${q.question_type_display})` : q.text)
        : (q.question_type_display || 'Untitled'),
    })),
  ];
  const selectedStillListed = !selected
    || options.some((o) => o.value && String(o.value) === String(selected));
  if (selected && !selectedStillListed) {
    options.push({ value: String(selected), label: 'Previously selected question' });
  }

  return (
    <div className="uni-calc-builder">
      <UniSelectField
        label="Show last answer of"
        required
        value={selected}
        onChange={(v) => set('last_answer_source', v)}
        options={options}
        hint="Which existing question this field replays. This field’s own name is the Question text above."
        help={{
          title: 'Show last answer of',
          body: 'Choose from questions already on this form. The Last Answer row keeps the name you typed — picking City here does not rename this field to City.',
        }}
      />
      {eligible.length < 1 && (
        <div className="uni-note uni-note--info">
          <span aria-hidden="true">💡</span>
          <span>
            Add at least one other question first, then come back and pick it here.
          </span>
        </div>
      )}
      <UniCheckField
        label="Allow respondent to edit this answer"
        checked={!!draft.last_answer_editable}
        onChange={(v) => set('last_answer_editable', v)}
        hint={draft.last_answer_editable
          ? 'The field still starts from their last answer, but they can type a different value.'
          : 'The field stays greyed out. Respondents can see the last answer but cannot change it.'}
        help={{
          title: 'Allow respondent to edit this answer',
          body: 'Off: the Last Answer field is read-only. On: it is pre-filled from their last completed answer and they may override it before submitting.',
        }}
      />
    </div>
  );
}

const DEFAULT_HIDDEN_SOURCES = [
  { value: 'static', label: 'Static / default value' },
  { value: 'ip', label: 'IP address' },
  { value: 'user_agent', label: 'Browser / app identifier' },
  { value: 'timestamp', label: 'Submission timestamp' },
  { value: 'location_required', label: 'Device location (required)' },
  { value: 'location_optional', label: 'Device location (optional)' },
];


/** A question's own default text, so a new row is never blank. */
function defaultTextFor(typeRow) {
  if (!typeRow) return '';
  return typeRow.label ? `${typeRow.label} question` : '';
}


/* ====================================================================
   Question-type palette
   ====================================================================
   Fetched once per mount from ``questions/types/``.  Returns the flat
   rows (for a <select>), the same rows grouped into <optgroup>s, and a
   lookup so a row can answer "does this type take options?" without the
   caller re-deriving it.

   Falls back to a single "Short Answer" entry if the request fails: a
   builder with an empty type dropdown can't create anything at all, and
   short text is the one type every form has.
   */
function useQuestionTypes() {
  const [state, setState] = React.useState({
    rows: [], groups: [], hiddenSources: DEFAULT_HIDDEN_SOURCES,
    loading: true, error: null,
  });

  React.useEffect(() => {
    let alive = true;
    getJSON(EP.TYPES)
      .then((d) => {
        if (!alive) return;
        setState({
          rows: d.types || [],
          groups: d.groups || [],
          hiddenSources: (d.hidden_sources && d.hidden_sources.length)
            ? d.hidden_sources
            : DEFAULT_HIDDEN_SOURCES,
          loading: false,
          error: null,
        });
      })
      .catch((error) => {
        if (!alive) return;
        setState({
          rows: [{ value: 'short_text', label: 'Short Answer', group: 'Standard',
                   supports_options: false }],
          groups: ['Standard'],
          hiddenSources: DEFAULT_HIDDEN_SOURCES,
          loading: false,
          error,
        });
      });
    return () => { alive = false; };
  }, []);

  const byValue = React.useMemo(() => {
    const map = {};
    state.rows.forEach((r) => { map[r.value] = r; });
    return map;
  }, [state.rows]);

  // ``[{ label: group, options: [...] }]`` — the shape ``UniGroupedSelect``
  // below wants.  Groups keep the palette's order (Standard first), which
  // is the order a form author is most likely to want.
  const grouped = React.useMemo(() => (
    (state.groups.length ? state.groups : ['Other']).map((g) => ({
      label: g,
      options: state.rows
        .filter((r) => (r.group || 'Other') === g)
        .map((r) => ({ value: r.value, label: r.label })),
    })).filter((g) => g.options.length > 0)
  ), [state.rows, state.groups]);

  return { ...state, byValue, grouped };
}


/**
 * A <select> with <optgroup>s.
 *
 * ``UniSelectField`` in ``UniShared.jsx`` renders a flat option list,
 * which is unusable for 35 field types — the user has to read the whole
 * thing to find "Star Rating".  Rather than complicate the shared
 * component for the one screen that needs grouping, this local variant
 * mirrors its markup (``.ty-field`` + ``.ty-select``) so the two look
 * identical side by side.
 */
function UniGroupedSelect({ label, value, onChange, groups, hint, help, required }) {
  const id = React.useId();
  return (
    <div className="ty-field">
      <FieldLabel htmlFor={id} label={label} required={required} help={help} />
      <select
        id={id}
        className="ty-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {groups.map((g) => (
          <optgroup key={g.label} label={g.label}>
            {g.options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </optgroup>
        ))}
      </select>
      {hint && <span className="uni-field__hint">{hint}</span>}
    </div>
  );
}


/**
 * Inline list editor for dropdown / multiple-choice / checkbox.
 *
 * The list used to live in a second modal that only opened *after* save,
 * so picking Dropdown looked like a type with nothing to configure.  This
 * is the list the respondent will see, edited in the same form as the
 * question text.
 */
function ChoiceOptionsEditor({ items, onChange, questionType }) {
  const rows = Array.isArray(items) && items.length ? items : emptyOptionItems();
  const isDropdown = questionType === 'dropdown';
  const filled = filledOptionItems(rows);
  const title = isDropdown ? 'Dropdown list items' : 'Answer choices';
  const help = isDropdown
    ? {
      title: 'What appears in the dropdown',
      body: 'Each row is one item in the menu. The respondent picks exactly one. Leave a row blank to ignore it; add more rows if the list is long.',
      where: 'Type the label the respondent should see, e.g. “Mumbai”, “Approved”, “Option A”. Order here is the order in the menu.',
    }
    : {
      title: 'What the respondent can pick',
      body: 'Each row is one choice on the form. Add at least two so there is something to choose between.',
      where: 'Type the label as it should appear next to the radio / checkbox. Tick “Other” if that row should open a free-text box.',
    };

  const update = (index, patch) => {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };
  const add = () => onChange([...rows, { text: '' }]);
  const remove = (index) => {
    if (rows.length <= 1) {
      onChange([{ text: '' }]);
      return;
    }
    onChange(rows.filter((_, i) => i !== index));
  };

  return (
    <div className="uni-option-list">
      <FieldLabel label={title} required help={help} />
      <span className="uni-field__hint">
        {isDropdown
          ? 'These labels are exactly what appears when the respondent opens the dropdown. Add every item you want them to pick from.'
          : 'These labels appear next to each choice. Add every option the respondent should be able to pick.'}
      </span>
      {rows.map((row, index) => (
        <div className="uni-option-list__row" key={row.id || `new-${index}`}>
          <span className="uni-option-list__index" aria-hidden="true">
            {index + 1}
          </span>
          <input
            className="ty-input"
            value={row.text || ''}
            onChange={(e) => update(index, { text: e.target.value })}
            placeholder={isDropdown
              ? `Item ${index + 1} (e.g. Option ${index + 1})`
              : `Choice ${index + 1}`}
            aria-label={`${title} ${index + 1}`}
          />
          <label className="uni-option-list__other">
            <input
              type="checkbox"
              checked={!!row.is_other}
              onChange={(e) => update(index, { is_other: e.target.checked })}
            />
            Other
          </label>
          <button
            type="button"
            className="ty-btn ty-btn--ghost ty-btn--sm"
            onClick={() => remove(index)}
            title="Remove this item"
          >
            Remove
          </button>
        </div>
      ))}
      <div className="uni-option-list__actions">
        <button
          type="button"
          className="ty-btn ty-btn--secondary ty-btn--sm"
          onClick={add}
        >
          + Add item
        </button>
        <span className="uni-field__hint">
          {filled.length === 0
            ? 'Add at least one item or the dropdown will have nothing to show.'
            : `${filled.length} item${filled.length === 1 ? '' : 's'} will appear on the form.`}
        </span>
      </div>
      {isDropdown && (
        <div className="uni-option-list__preview">
          <span className="uni-option-list__preview-label">Preview</span>
          <select className="ty-select" disabled aria-label="Dropdown preview">
            <option value="">
              {filled.length ? 'Choose…' : 'Add items to preview the dropdown'}
            </option>
            {filled.map((item, i) => (
              <option key={`${item.text}-${i}`} value={item.text}>
                {item.text}{item.is_other ? ' (Other)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}


/* ====================================================================
   The question fields, shared by Add and Edit
   ====================================================================
   Add and Edit collect exactly the same things, so they share one body.
   Which fields are *shown* depends on the chosen type — a Short Answer
   has no scale, a Section Header has no placeholder, and showing all ~50
   model fields at once would bury the four that matter.  Anything not
   rendered keeps its model default (on create) or its stored value (on
   edit, because ``useUniEdit`` PATCHes only what changed).
   */
function QuestionFields({ draft, set, types, isEdit, questions, selfId }) {
  const typeRow = types.byValue[draft.question_type] || null;
  const supportsOptions = !!typeRow?.supports_options;
  const guide = typeGuide(draft.question_type);
  const isLayout = LAYOUT_TYPES.includes(draft.question_type);
  const isScale = SCALE_TYPES.includes(draft.question_type);
  const isRating = RATING_TYPES.includes(draft.question_type);
  const isHtml = draft.question_type === 'html';
  const isConsent = draft.question_type === 'consent';
  const isHidden = draft.question_type === 'hidden';
  const isCalculation = draft.question_type === 'calculation';
  const isLastAnswer = draft.question_type === 'last_answer';
  const hiddenSource = draft.hidden_source || 'static';
  const isHiddenLocation = hiddenSource === 'location_required'
    || hiddenSource === 'location_optional';
  const isHiddenServerOwned = ['ip', 'user_agent', 'timestamp'].includes(hiddenSource);

  /* Changing the type re-labels a question that still has the old
     type's default text, so switching Short Answer → Star Rating
     doesn't leave "Short Answer question" on a row of stars.  Text the
     user actually typed is never touched.  Switching *to* a choice type
     also opens the list editor with two blank rows if none exist yet. */
  const onTypeChange = (value) => {
    const next = types.byValue[value] || null;
    set('question_type', value);
    if (next?.supports_options) {
      const items = Array.isArray(draft.option_items) ? draft.option_items : [];
      if (!items.length) set('option_items', emptyOptionItems());
    }
    if (value === 'hidden' && !draft.hidden_source) {
      set('hidden_source', 'static');
    }
    if (value === 'calculation') {
      const terms = Array.isArray(draft.calculation_terms) && draft.calculation_terms.length
        ? draft.calculation_terms
        : emptyCalculationTerms();
      set('calculation_terms', terms);
      if (draft.calculation_editable === '' || draft.calculation_editable === undefined) {
        set('calculation_editable', false);
      }
      if (draft.calculation_reset_enabled === '' || draft.calculation_reset_enabled === undefined) {
        set('calculation_reset_enabled', false);
      }
    }
    if (value === 'last_answer' && !isEdit) {
      set('last_answer_source', '');
      if (draft.last_answer_editable === '' || draft.last_answer_editable === undefined) {
        set('last_answer_editable', false);
      }
    }
    if (isEdit) return;                       // never rewrite saved wording
    const current = String(draft.text || '').trim();
    if (!current || current === defaultTextFor(typeRow)) {
      set('text', defaultTextFor(next));
    }
  };

  const onHiddenSourceChange = (value) => {
    set('hidden_source', value);
    if (value === 'location_required') set('is_required', true);
    if (value === 'location_optional') set('is_required', false);
  };

  return (
    <>
      <div className="uni-note uni-note--info">
        <span aria-hidden="true">💡</span>
        <span>
          Work top to bottom: pick the type, write the question, then fill
          any extra boxes that appear for that type (the dropdown list,
          scale ends, calculation chain, and so on). Hover or tap
          <strong> ? </strong> on a label for a short explanation.
        </span>
      </div>
      <UniGroupedSelect
        label="Question type"
        required
        value={draft.question_type || 'short_text'}
        onChange={onTypeChange}
        groups={types.grouped}
        hint={types.loading ? 'Loading the field palette…' : guide.hint}
        help={guide.help}
      />
      <UniTextField
        label={isLayout ? 'Heading / label' : 'Question text'}
        required
        value={draft.text || ''}
        onChange={(v) => set('text', v)}
        placeholder={isLayout
          ? 'e.g. About your visit'
          : isLastAnswer
            ? 'e.g. Last city'
            : 'e.g. How satisfied were you with the visit?'}
        hint={isLayout
          ? 'Shown as a heading on the form. Respondents do not answer this row.'
          : isLastAnswer
            ? 'This field’s own name. Respondents see this label, then the last answer to the question you pick below.'
            : 'This is the prompt the respondent sees. Keep it as the question you want answered.'}
        help={isLayout
          ? {
            title: 'Heading',
            body: 'Use a short title for this block of the form. Help text underneath can add a sentence of context.',
          }
          : isLastAnswer
            ? {
              title: 'Question text',
              body: 'Name this Last Answer field itself. It does not have to match the source question — “Last city” can replay “City”.',
              where: 'Example: “Last city” or “Previous score”.',
            }
            : {
              title: 'Question text',
              body: 'Write this as the respondent will read it. It is required even for layout blocks so the questions list stays scannable.',
              where: 'Example: “Which city are you based in?” or “Upload the invoice PDF”.',
            }}
      />
      {isLastAnswer && (
        <LastAnswerSourceEditor
          draft={draft}
          set={set}
          questions={questions}
          selfId={selfId}
        />
      )}
      <UniTextArea
        label="Help text under the question"
        rows={2}
        value={draft.description || ''}
        onChange={(v) => set('description', v)}
        placeholder="Shown in small print under the question."
        hint="Optional. Extra guidance for the respondent — not the list of dropdown items."
        help={{
          title: 'Help text',
          body: 'Appears in small print under the question on the form. Use it to explain format, examples, or why you are asking. Dropdown choices belong in List items, not here.',
          where: 'Example: “Pick the branch you visit most often.”',
        }}
      />

      {supportsOptions && (
        <ChoiceOptionsEditor
          items={draft.option_items}
          onChange={(next) => set('option_items', next)}
          questionType={draft.question_type}
        />
      )}

      {isHtml && (
        <UniTextArea
          label="HTML content"
          rows={4}
          value={draft.html_content || ''}
          onChange={(v) => set('html_content', v)}
          hint="Rendered as-is on the form. Use it for notices or images."
          help={{
            title: 'HTML content',
            body: 'Paste markup for a banner, instructions, or an image. This is not an answer field.',
          }}
        />
      )}

      {isHidden && (
        <UniSelectField
          label="Background value"
          required
          value={hiddenSource}
          onChange={onHiddenSourceChange}
          options={types.hiddenSources || DEFAULT_HIDDEN_SOURCES}
          hint={
            isHiddenLocation
              ? 'Captured from the respondent’s browser or the mobile app when they submit. They will be asked for location permission.'
              : isHiddenServerOwned
                ? 'Stamped on the server at submit time. The respondent never sees or types this.'
                : 'Not shown on the form. Use a default value or a URL prefill key (?campaign=…).'
          }
          help={{
            title: 'Background value',
            body: 'What to store without asking the respondent. Static uses the default / URL key; location reads GPS; IP, browser, and timestamp are stamped on the server.',
          }}
        />
      )}

      {isConsent && (
        <UniTextArea
          label="Consent wording"
          rows={2}
          value={draft.consent_text || ''}
          onChange={(v) => set('consent_text', v)}
          placeholder="I agree to the Terms and the Privacy Policy."
          hint="This sentence appears next to the tick-box on the form."
          help={{
            title: 'Consent wording',
            body: 'Write the legal sentence the respondent must agree to. Tick Required so they cannot submit without it.',
          }}
        />
      )}

      {isCalculation && (
        <CalculationTermsEditor
          draft={draft}
          set={set}
          questions={questions}
          selfId={selfId}
        />
      )}

      {/* A layout block has nothing to answer, so "required" and the
          input settings below would be meaningless on it. Hidden capture
          fields also skip these: required is implied by the source, and
          placeholder / prefill only apply to a static hidden value.
          Calculation answers are derived, so those input settings hide
          too — the chain editor above is the configuration.
          Last Answer is derived from a previous submission, so those
          input settings hide as well. */}
      {!isLayout && !isHidden && !isCalculation && !isLastAnswer && (
        <>
          <UniCheckField
            label="Required"
            checked={!!draft.is_required}
            onChange={(v) => set('is_required', v)}
            hint="The respondent can't submit without answering this."
            help={{
              title: 'Required',
              body: 'When on, the form cannot be sent until this question has an answer. Leave off for optional questions.',
            }}
          />
          <div className="uni-form-row">
            <UniTextField
              label="Placeholder"
              value={draft.placeholder || ''}
              onChange={(v) => set('placeholder', v)}
              placeholder="Grey hint text inside the input"
              hint={supportsOptions
                ? 'Shown as the empty dropdown label, e.g. “Select a city…”. Optional.'
                : 'Grey example text inside the empty box. Disappears when they start typing.'}
              help={{
                title: 'Placeholder',
                body: 'A hint inside the control, not an answer. For a dropdown it is the first “Choose…” line.',
              }}
            />
            <UniTextField
              label="Default value"
              value={draft.default_value || ''}
              onChange={(v) => set('default_value', v)}
              placeholder="Pre-filled answer"
              hint={supportsOptions
                ? 'Must match one list item exactly if you want that item pre-selected.'
                : 'The box starts with this text. The respondent can still change it.'}
              help={{
                title: 'Default value',
                body: 'Pre-fills the answer. For dropdown / multiple choice it should be the exact label of one list item.',
              }}
            />
          </div>
          <UniTextField
            label="Prefill key"
            value={draft.prefill_param || ''}
            onChange={(v) => set('prefill_param', v)}
            placeholder="e.g. email, first_name, city"
            hint={'URL query param (?email=…) or signed-in profile field. '
              + 'Keys: email, first_name, last_name, full_name, city, state, '
              + 'phone_number, username, division, role.'}
            help={{
              title: 'Prefill key',
              body: 'Fills the question from a URL (?city=Pune) or from the signed-in profile when the key matches a known field.',
              where: 'Use a short slug with no spaces. Example: city, email, campaign.',
            }}
          />
          <UniCheckField
            label="Show last answer"
            checked={!!draft.show_last_submission}
            onChange={(v) => set('show_last_submission', v)}
            hint="Signed-in respondents can see what they submitted for this question last time. Only their own last submission of this question."
            help={{
              title: 'Show last answer',
              body: 'A reminder of what this person answered previously. It does not change the new answer unless you also tick Pre-fill last answer.',
            }}
          />
          <UniCheckField
            label="Pre-fill last answer"
            checked={!!draft.prefill_last_submission}
            onChange={(v) => set('prefill_last_submission', v)}
            hint="Pre-fill this input with that same user's last submission of this question."
            help={{
              title: 'Pre-fill last answer',
              body: 'Starts the box with this user’s previous reply so they only change it if something is different.',
            }}
          />
        </>
      )}

      {isHidden && hiddenSource === 'static' && (
        <>
          <UniTextField
            label="Default value"
            value={draft.default_value || ''}
            onChange={(v) => set('default_value', v)}
            placeholder="Submitted if the URL does not override it"
            hint="Stored with the response when the URL does not supply a prefill key."
            help={{
              title: 'Hidden default',
              body: 'The value saved if nothing in the URL overrides it. Use this for a campaign code that is the same for everyone.',
            }}
          />
          <UniTextField
            label="Prefill key"
            value={draft.prefill_param || ''}
            onChange={(v) => set('prefill_param', v)}
            placeholder="e.g. campaign, utm_source"
            hint="URL query param that fills this hidden field, e.g. ?campaign=spring."
            help={{
              title: 'Hidden prefill key',
              body: 'The name of the query parameter that overwrites the default, e.g. campaign in ?campaign=spring.',
            }}
          />
        </>
      )}

      {isScale && (
        <>
          <div className="uni-form-row">
            <UniTextField
              label="Scale from"
              value={String(draft.scale_min ?? 1)}
              onChange={(v) => set('scale_min', v.replace(/[^0-9-]/g, ''))}
              hint="Lowest number on the scale, usually 0 or 1."
              help={{
                title: 'Scale from',
                body: 'The left-hand number. Keep it lower than Scale to.',
              }}
            />
            <UniTextField
              label="Scale to"
              value={String(draft.scale_max ?? 5)}
              onChange={(v) => set('scale_max', v.replace(/[^0-9-]/g, ''))}
              hint="Highest number on the scale, usually 5 or 10."
              help={{
                title: 'Scale to',
                body: 'The right-hand number. Must be greater than Scale from.',
              }}
            />
          </div>
          <div className="uni-form-row">
            <UniTextField
              label="Label for the lowest value"
              value={draft.scale_min_label || ''}
              onChange={(v) => set('scale_min_label', v)}
              placeholder="e.g. Not at all"
              hint="Optional words under the lowest number, e.g. “Poor”."
              help={{
                title: 'Low-end label',
                body: 'Shown next to the smallest number so the scale is readable without a legend.',
              }}
            />
            <UniTextField
              label="Label for the highest value"
              value={draft.scale_max_label || ''}
              onChange={(v) => set('scale_max_label', v)}
              placeholder="e.g. Extremely"
              hint="Optional words under the highest number, e.g. “Excellent”."
              help={{
                title: 'High-end label',
                body: 'Shown next to the largest number. Leave blank if the numbers are self-explanatory.',
              }}
            />
          </div>
        </>
      )}

      {isRating && (
        <UniTextField
          label="Number of stars"
          value={String(draft.rating_max ?? 5)}
          onChange={(v) => set('rating_max', v.replace(/[^0-9]/g, ''))}
          hint="Usually 3, 5 or 10."
          help={{
            title: 'Number of stars',
            body: 'How many stars (or hearts) to show. 5 is the usual default.',
          }}
        />
      )}

      {!isLayout && !isHidden && !isCalculation && !isLastAnswer && (
        <>
          <UniSelectField
            label="Answer validation"
            value={draft.validation_type ?? ''}
            onChange={(v) => set('validation_type', v)}
            options={VALIDATION_OPTIONS}
            hint="Extra check on top of the question type. Skip this for dropdowns — the list already limits the answer."
            help={{
              title: 'Answer validation',
              body: 'Optional extra rule (must be a number, an email, a minimum length, and so on). Most types already validate themselves; leave this on “No extra validation” unless you need a tighter rule.',
            }}
          />
          {/* Only the rule types that need a value show one — "must be an
              email" has nothing to configure. */}
          {['regex', 'min_length', 'max_length'].includes(draft.validation_type) && (
            <UniTextField
              label="Validation value"
              value={draft.validation_value || ''}
              onChange={(v) => set('validation_value', v)}
              placeholder={draft.validation_type === 'regex'
                ? 'e.g. ^[A-Z]{3}-[0-9]+$'
                : 'e.g. 10'}
              hint={draft.validation_type === 'regex'
                ? 'A regular expression the answer must match.'
                : 'The length limit, in characters.'}
              help={{
                title: 'Validation value',
                body: 'The number or pattern the chosen rule uses. For min/max length this is a count of characters.',
              }}
            />
          )}
        </>
      )}

      {!isHidden && (
      <div className="uni-form-row">
        <UniSelectField
          label="Width on desktop"
          value={draft.width_desktop || 'full'}
          onChange={(v) => set('width_desktop', v)}
          options={WIDTH_OPTIONS}
          hint="How wide this question is on a large screen. Two half-width questions sit side by side."
          help={{
            title: 'Desktop width',
            body: 'Full width is safest. Use Medium (50%) when two short questions should share a row.',
          }}
        />
        <UniSelectField
          label="Width on mobile"
          value={draft.width_mobile || 'full'}
          onChange={(v) => set('width_mobile', v)}
          options={WIDTH_OPTIONS}
          hint="On phones, even “small” stays at least half-width so the field stays tappable."
          help={{
            title: 'Mobile width',
            body: 'Leave this on Full width unless you have two very short fields that should share a row on a phone.',
          }}
        />
      </div>
      )}
    </>
  );
}


/* ====================================================================
   Add / Edit modals
   ==================================================================== */

function lastAnswerReady(draft) {
  if (draft.question_type !== 'last_answer') return true;
  return Boolean(draft.last_answer_source);
}

function questionTextReady(draft) {
  return String(draft.text || '').trim().length > 0;
}

function calculationReady(draft) {
  if (draft.question_type !== 'calculation') return true;
  const terms = Array.isArray(draft.calculation_terms) ? draft.calculation_terms : [];
  return terms.filter((t) => t && t.question_id).length >= 2;
}

function optionsReady(draft, types) {
  const typeRow = types?.byValue?.[draft.question_type];
  if (!typeRow?.supports_options) return true;
  return filledOptionItems(draft.option_items).length >= 1;
}

function questionPayload(draft, types, questions) {
  const out = { ...draft, text: String(draft.text || '').trim() };
  ['scale_min', 'scale_max', 'rating_max'].forEach((k) => {
    if (out[k] === '' || out[k] === null || out[k] === undefined) delete out[k];
    else out[k] = Number(out[k]);
  });
  if (out.question_type === 'calculation') {
    out.calculation_editable = !!out.calculation_editable;
    out.calculation_terms = (out.calculation_terms || []).filter(
      (t) => t && t.question_id,
    );
    out.calculation_reset_enabled = !!out.calculation_reset_enabled;
    out.calculation_reset_weekday = Number(out.calculation_reset_weekday ?? 0);
    out.calculation_reset_day = Number(out.calculation_reset_day || 1);
    out.calculation_reset_month = Number(out.calculation_reset_month || 1);
    out.calculation_reset_period = out.calculation_reset_enabled
      ? (out.calculation_reset_period || 'weekly')
      : '';
  }
  if (out.question_type === 'last_answer') {
    out.last_answer_source = out.last_answer_source || null;
    out.last_answer_editable = !!out.last_answer_editable;
    out.is_required = false;
  } else {
    delete out.last_answer_source;
  }
  if (types?.byValue?.[out.question_type]?.supports_options) {
    out.option_items = filledOptionItems(out.option_items);
  } else {
    delete out.option_items;
  }
  return out;
}


function AddQuestionModal({ create, types, questions }) {
  const { open, draft, busy, error, close, set, submit } = create;
  const canSave = questionTextReady(draft)
    && calculationReady(draft)
    && lastAnswerReady(draft)
    && optionsReady(draft, types);

  return (
    <UniModal
      open={open}
      title="Add question"
      onClose={close}
      width={620}
      footer={
        <>
          <button type="button" className="ty-btn ty-btn--secondary"
                  onClick={close} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="ty-btn"
                  onClick={() => submit(questionPayload(draft, types, questions))}
                  disabled={busy || !canSave}>
            {busy ? 'Adding…' : 'Add question'}
          </button>
        </>
      }
    >
      <UniFormError error={error} />
      <QuestionFields draft={draft} set={set} types={types} questions={questions} />
      {types.byValue[draft.question_type]?.supports_options
        && filledOptionItems(draft.option_items).length < 1 && (
        <div className="uni-note uni-note--warn">
          <span aria-hidden="true">⚠️</span>
          <span>
            Add at least one list item above. A dropdown with an empty list
            cannot be answered.
          </span>
        </div>
      )}
      {/* ``order`` is intentionally not collected: the server appends the
          new question to the end (``QuestionViewSet.perform_create``), and
          the row's ↑ / ↓ buttons are how you move it afterwards. */}
      <div className="uni-note uni-note--info">
        <span aria-hidden="true">💡</span>
        <span>
          The question is added at the end of the form. Drag its
          <strong> :: </strong> handle, or use the
          <strong> ↑ </strong> / <strong> ↓ </strong> buttons, to move it.
          {types.byValue[draft.question_type]?.supports_options
            ? ' Dropdown / choice lists are saved with the question — you do not need a second step.'
            : ''}
        </span>
      </div>
    </UniModal>
  );
}


function EditQuestionModal({ edit, types, questions }) {
  const { open, draft, busy, error, close, set, submit } = edit;
  const canSave = questionTextReady(draft)
    && calculationReady(draft)
    && lastAnswerReady(draft)
    && optionsReady(draft, types);

  return (
    <UniModal
      open={open}
      title="Edit question"
      onClose={close}
      width={620}
      footer={
        <>
          <button type="button" className="ty-btn ty-btn--secondary"
                  onClick={close} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="ty-btn"
                  onClick={() => submit(questionPayload(draft, types, questions))}
                  disabled={busy || !canSave}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      <UniFormError error={error} />
      <QuestionFields
        draft={draft}
        set={set}
        types={types}
        isEdit
        questions={questions}
        selfId={edit.row?.id}
      />
      {types.byValue[draft.question_type]?.supports_options
        && filledOptionItems(draft.option_items).length < 1 && (
        <div className="uni-note uni-note--warn">
          <span aria-hidden="true">⚠️</span>
          <span>
            Add at least one list item above. A dropdown with an empty list
            cannot be answered.
          </span>
        </div>
      )}
    </UniModal>
  );
}


/* ====================================================================
   Options manager
   ====================================================================
   A multiple-choice question is only half-created until it has choices,
   so this is a nested editor rather than a separate screen: you open it
   from the question's row, add / rename / delete options, and close.

   Each option is its own PATCH / DELETE against
   ``/api/uni-forms/options/`` (they're independent rows and there's no
   batch endpoint), but the *list* is re-read after each change so what's
   on screen is always what's stored — an optimistic local edit that
   silently failed would be worse here than a brief spinner.
   */
function OptionsModal({ question, onClose, onChanged, canEdit }) {
  const url = question ? `${EP.OPTIONS}?question=${question.id}&page_size=200` : '';
  const { rows, loading, error, reload } = useUniList(url, [question?.id]);
  const [text, setText] = React.useState('');
  const [isOther, setIsOther] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [editingText, setEditingText] = React.useState('');
  const act = useUniAction();

  // Clear the composer when the modal is pointed at a different question,
  // so a half-typed option can't leak onto the next one.
  React.useEffect(() => {
    setText('');
    setIsOther(false);
    setEditingId(null);
  }, [question?.id]);

  const done = () => { reload(); onChanged?.(); };

  const add = () => {
    const value = text.trim();
    if (!value) return;
    act.run(async () => {
      await postJSON(EP.OPTIONS, {
        question: question.id, text: value, is_other: isOther,
      });
      setText('');
      setIsOther(false);
      done();
    }).catch(() => {});
  };

  const saveEdit = () => {
    const value = editingText.trim();
    if (!value || !editingId) { setEditingId(null); return; }
    act.run(async () => {
      await patchJSON(`${EP.OPTIONS}${editingId}/`, { text: value });
      setEditingId(null);
      done();
    }).catch(() => {});
  };

  const remove = (row) => {
    act.run(async () => {
      await deleteJSON(`${EP.OPTIONS}${row.id}/`);
      done();
    }).catch(() => {});
  };

  const columns = [
    { key: 'order', label: '#', align: 'right',
      render: (r) => <span className="uni-num--muted">{r.order}</span> },
    { key: 'text', label: 'Option',
      render: (r) => (
        editingId === r.id
          ? (
            <input
              className="ty-input"
              value={editingText}
              autoFocus
              onChange={(e) => setEditingText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveEdit();
                if (e.key === 'Escape') setEditingId(null);
              }}
            />
          )
          : <span style={{ fontWeight: 600 }}>{r.text}</span>
      ) },
    { key: 'is_other', label: '"Other"?', align: 'center',
      render: (r) => <UniBool value={r.is_other} /> },
    { key: 'actions', label: '', align: 'right',
      render: (r) => (
        <UniRowActions items={editingId === r.id ? [
          { key: 'save', label: 'Save', title: 'Save this option',
            show: canEdit, busy: act.busy, onClick: saveEdit },
          { key: 'cancel', label: 'Cancel',
            onClick: () => setEditingId(null) },
        ] : [
          { key: 'rename', label: 'Rename', title: 'Change this option\'s text',
            show: canEdit,
            onClick: () => { setEditingId(r.id); setEditingText(r.text || ''); } },
          { key: 'delete', label: 'Delete', danger: true, show: canEdit,
            title: 'Remove this option', busy: act.busy,
            onClick: () => remove(r) },
        ]} />
      ) },
  ];

  return (
    <UniModal
      open={!!question}
      title={question ? `Options — ${question.text}` : 'Options'}
      onClose={onClose}
      width={620}
      footer={
        <button type="button" className="ty-btn ty-btn--secondary" onClick={onClose}>
          Done
        </button>
      }
    >
      <UniFormError error={act.error} />
      <p className="uni-field__hint" style={{ marginTop: 0 }}>
        Each row is one item in the dropdown or choice list. Add at least
        two so the respondent has something to pick. You can also edit this
        list in the question’s Add / Edit form.
      </p>
      <UniTable
        rows={rows} loading={loading} error={error} columns={columns} rowKey="id"
        empty="No options yet"
        emptyHint="A choice question with no options can't be answered — add at least two."
      />
      {canEdit && (
        <div style={{ marginTop: 14 }}>
          <UniSection title="Add an option" />
          <UniTextField
            label="Option text"
            value={text}
            onChange={setText}
            placeholder="e.g. Very satisfied"
            hint="This label is what the respondent sees in the list."
            help={{
              title: 'Option text',
              body: 'One choice on the question. For a dropdown this is one row in the menu.',
            }}
          />
          <UniCheckField
            label='Treat as an "Other" option'
            checked={isOther}
            onChange={setIsOther}
            hint="Respondents get a free-text box when they pick this."
            help={{
              title: 'Other',
              body: 'Use this for a catch-all choice. When picked, the form shows a box so they can type their own answer.',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className="ty-btn"
                    onClick={add} disabled={act.busy || !text.trim()}>
              {act.busy ? 'Adding…' : '+ Add option'}
            </button>
          </div>
        </div>
      )}
    </UniModal>
  );
}


/** Read-only "View" drawer — everything the row's columns can't fit. */
function ViewQuestionModal({ row, questions, onClose }) {
  return (
    <UniModal
      open={!!row}
      title="Question details"
      onClose={onClose}
      width={620}
      footer={
        <button type="button" className="ty-btn ty-btn--secondary" onClick={onClose}>
          Close
        </button>
      }
    >
      {row && (
        <>
          <UniDetailRow label="Position">{row.order}</UniDetailRow>
          <UniDetailRow label="Question">{row.text}</UniDetailRow>
          {row.question_type === 'last_answer' && (
            <>
              <UniDetailRow label="Shows last answer of">
                {(questions || []).find(
                  (q) => String(q.id) === String(row.last_answer_source),
                )?.text || '—'}
              </UniDetailRow>
              <UniDetailRow label="Answer editable">
                <UniBool value={!!row.last_answer_editable} />
              </UniDetailRow>
            </>
          )}
          <UniDetailRow label="Help text">{row.description}</UniDetailRow>
          <UniDetailRow label="Type">
            <UniStatusPill value={row.question_type_display || row.question_type}
                           palette="cyan" />
          </UniDetailRow>
          {row.question_type === 'calculation' && (
            <>
              <UniDetailRow label="Calculation">
                {formulaLabel(row.calculation_terms || [], questions || [])
                  || row.calculation_formula || '—'}
              </UniDetailRow>
              <UniDetailRow label="Answer editable">
                <UniBool value={!!row.calculation_editable} />
              </UniDetailRow>
              <UniDetailRow label="Reset">
                {row.calculation_reset_enabled
                  ? calculationResetScheduleLabel(row)
                  : 'Off'}
              </UniDetailRow>
            </>
          )}
          <UniDetailRow label="Required">
            <UniBool value={row.is_required} />
          </UniDetailRow>
          <UniDetailRow label="Placeholder">{row.placeholder}</UniDetailRow>
          <UniDetailRow label="Default value">{row.default_value}</UniDetailRow>
          {row.prefill_param && (
            <UniDetailRow label="Prefill key">{row.prefill_param}</UniDetailRow>
          )}
          <UniDetailRow label="Show last answer">
            <UniBool value={row.show_last_submission} />
          </UniDetailRow>
          <UniDetailRow label="Pre-fill last answer">
            <UniBool value={row.prefill_last_submission} />
          </UniDetailRow>
          {row.supports_options && (
            <UniDetailRow label="Answer options">
              {(row.options || []).length
                ? (row.options || []).map((o) => o.text).join(', ')
                : 'None yet'}
            </UniDetailRow>
          )}
          {SCALE_TYPES.includes(row.question_type) && (
            <UniDetailRow label="Scale">
              {row.scale_min} – {row.scale_max}
              {(row.scale_min_label || row.scale_max_label) && (
                <span className="uni-field__hint">
                  {row.scale_min_label || '—'} → {row.scale_max_label || '—'}
                </span>
              )}
            </UniDetailRow>
          )}
          {RATING_TYPES.includes(row.question_type) && (
            <UniDetailRow label="Stars">{row.rating_max}</UniDetailRow>
          )}
          <UniDetailRow label="Validation">
            {row.validation_type
              ? `${row.validation_type}${row.validation_value ? `: ${row.validation_value}` : ''}`
              : 'None'}
          </UniDetailRow>
          <UniDetailRow label="Width">
            {`${row.width_desktop || 'full'} on desktop, ${row.width_mobile || 'full'} on mobile`}
          </UniDetailRow>
          <UniDetailRow label="Last updated">
            {row.updated_at ? new Date(row.updated_at).toLocaleString() : ''}
          </UniDetailRow>
        </>
      )}
    </UniModal>
  );
}


/* ====================================================================
   Question list with drag-and-drop reorder
   ====================================================================
   ``UniTable`` is a plain renderer; reordering needs row-level drag
   state and a grip column.  One atomic ``POST …/reorder/`` persists
   the whole order — same contract the legacy Forminator builder used.
   */
function reorderList(items, fromIndex, toIndex) {
  if (fromIndex === toIndex) return items;
  const next = [...items];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

function QuestionOrderTable({
  rows, loading, error, empty, emptyHint, columns, rowKey,
  canDrag, onReorder, reordering,
}) {
  const [dragIndex, setDragIndex] = React.useState(null);
  const [overIndex, setOverIndex] = React.useState(null);
  const enableSort = !canDrag;
  const { sortedRows, sort, toggleSort } = useTableSort(rows, {
    columns,
    sortable: enableSort,
  });

  React.useEffect(() => {
    setDragIndex(null);
    setOverIndex(null);
  }, [rows]);

  if (loading) {
    return (
      <div className="uni-state">
        <div className="ty-spinner" aria-hidden="true" />
        <div className="uni-state__hint">Loading…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="ty-error">
        <div className="ty-error__head">
          <span className="ty-error__icon">⚠️</span>
          Couldn't load this list.
        </div>
        <div className="ty-error__trail">{String(error.message || error)}</div>
      </div>
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <div className="uni-state">
        <div className="uni-state__icon">📭</div>
        <div className="uni-state__title">{empty || 'Nothing here yet.'}</div>
        {emptyHint && <div className="uni-state__hint">{emptyHint}</div>}
      </div>
    );
  }

  const finishDrag = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleDrop = (dropIndex) => {
    if (!canDrag || dragIndex === null || dragIndex === dropIndex || reordering) {
      finishDrag();
      return;
    }
    onReorder(dragIndex, dropIndex);
    finishDrag();
  };

  return (
    <div className="uni-table ty-table-wrap">
      <table className="ty-table uni-question-table">
        <thead>
          <tr>
            {canDrag && (
              <th className="uni-question-table__grip-head" aria-label="Drag to reorder" />
            )}
            {columns.map((c) => (
              <SortableTh
                key={c.key}
                label={c.label}
                sortKey={c.key}
                activeKey={sort.key}
                direction={sort.dir}
                onSort={toggleSort}
                sortable={enableSort && columnIsSortable(c)}
                align={c.align || 'left'}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((r, i) => {
            const key = (rowKey && r[rowKey]) || i;
            const rowClass = [
              dragIndex === i ? 'uni-question-row--dragging' : '',
              overIndex === i && dragIndex !== i ? 'uni-question-row--drag-over' : '',
            ].filter(Boolean).join(' ');

            return (
              <tr
                key={key}
                className={rowClass || undefined}
                onDragOver={(e) => {
                  if (!canDrag || dragIndex === null) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  setOverIndex(i);
                }}
                onDragLeave={() => {
                  setOverIndex((current) => (current === i ? null : current));
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  handleDrop(i);
                }}
              >
                {canDrag && (
                  <td className="uni-question-table__grip-cell">
                    <span
                      className="uni-question-drag-handle"
                      draggable={!reordering}
                      aria-label="Drag to reorder"
                      title={reordering ? 'Saving order…' : 'Drag to reorder'}
                      onDragStart={(e) => {
                        if (reordering) {
                          e.preventDefault();
                          return;
                        }
                        setDragIndex(i);
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', String(i));
                      }}
                      onDragEnd={finishDrag}
                    >
                      ::
                    </span>
                  </td>
                )}
                {columns.map((c) => (
                  <td
                    key={c.key}
                    data-label={c.label}
                    style={{ textAlign: c.align || 'left' }}
                  >
                    {c.render ? c.render(r) : (r[c.key] ?? '—')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


/* ====================================================================
   The page
   ==================================================================== */

/** The fields Edit collects — kept in one place so ``edit.start`` and
    the modal can't disagree about what's editable. */
const EDIT_FIELDS = [
  'question_type', 'text', 'description', 'is_required',
  'placeholder', 'default_value', 'prefill_param', 'hidden_source',
  'scale_min', 'scale_max', 'scale_min_label', 'scale_max_label',
  'rating_max',   'html_content', 'consent_text',
  'calculation_terms', 'calculation_editable',
  'calculation_reset_enabled', 'calculation_reset_period',
  'calculation_reset_weekday', 'calculation_reset_day',
  'calculation_reset_month',
  'last_answer_source', 'last_answer_editable',
  'validation_type', 'validation_value',
  'width_desktop', 'width_mobile',
  'option_items',
];


export function UniFormsQuestions() {
  const { formId } = useParams();
  const [q, setQ] = React.useState('');
  const [viewing, setViewing] = React.useState(null);
  const [optionsFor, setOptionsFor] = React.useState(null);

  // DRF paginates at 20/page by default; a form builder must show every
  // question at once (reorder, duplicate, option counts) — same pattern as
  // ``UniFormsReportTemplateWizard`` and ``UniFormsAdminPages``.
  const listUrl = formId
    ? `${EP.QUESTIONS}?form=${formId}&page_size=200${q ? `&search=${encodeURIComponent(q)}` : ''}`
    : '';
  const { rows, loading, error, raw, reload } = useUniList(listUrl, [formId, q]);
  const [orderedRows, setOrderedRows] = React.useState([]);
  const [reordering, setReordering] = React.useState(false);
  React.useEffect(() => { setOrderedRows(rows); }, [rows]);
  const types = useQuestionTypes();
  const act = useUniAction();

  // The parent form, for the page title and — more importantly — for
  // ``can_edit``.  Each question row carries its own copy, but an empty
  // form has no rows to carry it, and "Add question" has to be decided
  // before the first one exists.
  const [form, setForm] = React.useState(null);
  const [formError, setFormError] = React.useState(null);
  React.useEffect(() => {
    if (!formId) return undefined;
    let alive = true;
    getJSON(`${EP.FORMS}${formId}/`)
      .then((d) => { if (alive) setForm(d); })
      .catch((err) => { if (alive) setFormError(err); });
    return () => { alive = false; };
  }, [formId]);

  const canEdit = form ? form.can_edit !== false : false;

  const create = useUniCreate(
    EP.QUESTIONS,
    {
      form: formId,
      question_type: 'short_text',
      text: '',
      description: '',
      is_required: false,
      placeholder: '',
      default_value: '',
      prefill_param: '',
      show_last_submission: false,
      prefill_last_submission: false,
      hidden_source: 'static',
      scale_min: 1,
      scale_max: 5,
      scale_min_label: '',
      scale_max_label: '',
      rating_max: 5,
      html_content: '',
      consent_text: '',
      calculation_terms: emptyCalculationTerms(),
      calculation_editable: false,
      calculation_reset_enabled: false,
      calculation_reset_period: '',
      calculation_reset_weekday: 0,
      calculation_reset_day: 1,
      calculation_reset_month: 1,
      last_answer_source: '',
      last_answer_editable: false,
      validation_type: '',
      validation_value: '',
      width_desktop: 'full',
      width_mobile: 'full',
      option_items: emptyOptionItems(),
    },
    (created) => {
      reload();
      // Only open the dedicated Options editor when the question still
      // has no list — the create modal now collects dropdown items itself.
      if (created && created.supports_options && !(created.options || []).length) {
        setOptionsFor(created);
      }
    },
  );
  const edit = useUniEdit(EP.QUESTIONS, () => reload());
  const del  = useUniDelete(EP.QUESTIONS, () => reload());

  // Which row is mid-request.  ``act.busy`` is page-wide, so using it for
  // the row buttons would spin the arrows on all 30 rows while one moves.
  const [busyId, setBusyId] = React.useState(null);

  const rowAction = (row, fn) => {
    setBusyId(row.id);
    return act.run(fn)
      .then(() => reload())
      .catch(() => {})
      .finally(() => setBusyId(null));
  };

  const move = (row, direction) => rowAction(row, () =>
    postJSON(`${EP.QUESTIONS}${row.id}/move/`, { direction }));

  const duplicate = (row) => rowAction(row, () =>
    postJSON(`${EP.QUESTIONS}${row.id}/duplicate/`, {}));

  const handleReorder = (fromIndex, toIndex) => {
    if (!canEdit || q || fromIndex === toIndex) return;
    const next = reorderList(orderedRows, fromIndex, toIndex);
    const order = next.map((r) => r.id);
    setOrderedRows(next);
    setReordering(true);
    act.run(() => postJSON(EP.REORDER, { form: formId, order }))
      .then(() => reload())
      .catch(() => setOrderedRows(rows))
      .finally(() => setReordering(false));
  };

  const canDrag = canEdit && !q && orderedRows.length > 1;

  const columns = [
    { key: 'order', label: '#', align: 'right',
      render: (r) => <span className="uni-num">{r.order}</span> },
    { key: 'text', label: 'Question',
      render: (r) => (
        <span>
          <span style={{ fontWeight: 600 }}>{r.text}</span>
          {r.description && (
            <span className="uni-field__hint">{r.description}</span>
          )}
        </span>
      ) },
    { key: 'question_type', label: 'Type',
      render: (r) => (
        <UniStatusPill value={r.question_type_display || r.question_type}
                       palette="cyan" />
      ) },
    { key: 'is_required', label: 'Required?', align: 'center',
      render: (r) => <UniBool value={r.is_required} /> },
    // Only choice questions have options, so an em-dash here means
    // "not applicable" and a red 0 means "this question is unanswerable".
    { key: 'option_count', label: 'Options', align: 'right',
      render: (r) => {
        if (!r.supports_options) return <span className="uni-num--muted">—</span>;
        if (!r.option_count) {
          return (
            <span className="uni-num" style={{ color: 'var(--ty-danger)' }}
                  title="This question has no answer choices — nobody can answer it.">
              0 ⚠
            </span>
          );
        }
        return <span className="uni-num">{fmtCount(r.option_count)}</span>;
      } },
    { key: 'actions', label: 'Actions', align: 'right',
      render: (r) => (
        <UniRowActions items={[
          { key: 'up', label: '↑', title: 'Move this question up',
            show: r.can_edit !== false, busy: busyId === r.id,
            onClick: () => move(r, 'up') },
          { key: 'down', label: '↓', title: 'Move this question down',
            show: r.can_edit !== false, busy: busyId === r.id,
            onClick: () => move(r, 'down') },
          { key: 'options',
            label: r.option_count ? `Options (${r.option_count})` : 'Options',
            title: 'Add or edit this question\'s answer choices',
            // Hidden rather than disabled on types that take no options:
            // a permanently-dead button is noise, not a hint.
            show: !!r.supports_options,
            onClick: () => setOptionsFor(r) },
          { key: 'view', label: 'View', title: 'See every setting on this question',
            onClick: () => setViewing(r) },
          { key: 'edit', label: 'Edit', title: 'Change this question',
            show: r.can_edit !== false,
            onClick: () => edit.start({
              ...r,
              option_items: optionItemsFromQuestion(r),
            }, EDIT_FIELDS) },
          { key: 'duplicate', label: 'Duplicate',
            title: 'Copy this question, with its options, just below',
            show: r.can_edit !== false, busy: busyId === r.id,
            onClick: () => duplicate(r) },
          { key: 'delete', label: 'Delete', danger: true,
            title: 'Remove this question from the form',
            show: r.can_delete !== false,
            onClick: () => del.ask(r) },
        ]} />
      ) },
  ];

  const title = form?.title ? `Questions — ${form.title}` : 'Questions';
  const missingOptions = rows.filter(
    (r) => r.supports_options && !r.option_count,
  ).length;

  return (
    <UniPageShell
      theme={THEME}
      icon="❓"
      title={title}
      subtitle={raw?.count != null
        ? `${fmtCount(raw.count)} question${raw.count === 1 ? '' : 's'}`
        : 'What this form asks'}
      actions={
        <>
          {canEdit && (
            <UniActionButton hero primary onClick={create.start}>
              + Add Question
            </UniActionButton>
          )}
          <UniActionButton hero to={ROUTES.UNI_FORMS_FORM_RESPONSES(formId)}>
            📥 Responses
          </UniActionButton>
          <UniActionButton hero to="/uni-forms/forms">← Forms</UniActionButton>
        </>
      }
    >
      {formError && (
        <div className="ty-error" style={{ marginBottom: 12 }}>
          <div className="ty-error__head">
            <span className="ty-error__icon">⚠️</span>
            Couldn't load this form.
          </div>
          <div className="ty-error__trail">
            {String(formError.message || formError)}
          </div>
        </div>
      )}

      {/* A read-only visitor still gets the list (so they can see what the
          form asks) but no verbs — and the reason is spelled out, because
          "where are the buttons?" is otherwise a support ticket. */}
      {form && !canEdit && (
        <div className="uni-note uni-note--lock">
          <span aria-hidden="true">🔒</span>
          <span>
            You have read-only access to <strong>{form.title}</strong>, so its
            questions can't be changed here. Ask its owner to share it with
            edit rights, or an administrator.
          </span>
        </div>
      )}

      {canEdit && (
        <div className="uni-note uni-note--info">
          <span aria-hidden="true">💡</span>
          <span>
            Click <strong>+ Add Question</strong>, pick a type, and fill
            the boxes that appear. For a <strong>Dropdown</strong>, the
            list items editor opens in that same form — those labels are
            what respondents see in the menu. Tap <strong>?</strong> on
            any label for a short explanation.
          </span>
        </div>
      )}
      {form?.status === 'draft' && canEdit && (
        <div className="uni-note uni-note--info">
          <span aria-hidden="true">💡</span>
          <span>
            This form is still a <strong>draft</strong>. Add the questions you
            need, then set its status to <strong>Published</strong> on the
            Forms list to start collecting responses.
          </span>
        </div>
      )}

      {missingOptions > 0 && (
        <div className="uni-note uni-note--warn">
          <span aria-hidden="true">⚠️</span>
          <span>
            {missingOptions === 1
              ? 'One choice question has no answer options'
              : `${missingOptions} choice questions have no answer options`}
            {' '}— respondents won't be able to answer
            {missingOptions === 1 ? ' it' : ' them'}. Open
            <strong> Edit </strong> and fill the list items, or use the
            <strong> Options </strong> button on the affected row
            {missingOptions === 1 ? '' : 's'}.
          </span>
        </div>
      )}

      {canDrag && (
        <div className="uni-note uni-note--info">
          <span aria-hidden="true">↕️</span>
          <span>
            Drag the <strong>::</strong> handle on a row to move it. The
            <strong> ↑ </strong> / <strong> ↓ </strong> buttons still work
            for small adjustments.
          </span>
        </div>
      )}

      {canEdit && q && orderedRows.length > 1 && (
        <div className="uni-note uni-note--info">
          <span aria-hidden="true">ℹ️</span>
          <span>
            Clear the search box to drag-and-drop reorder questions.
          </span>
        </div>
      )}

      <UniFormError error={act.error} />

      <UniToolbar onSearch={setQ} placeholder="Search questions…">
        {canEdit && (
          <UniActionButton primary onClick={create.start}>
            + Add Question
          </UniActionButton>
        )}
      </UniToolbar>

      <QuestionOrderTable
        rows={orderedRows}
        loading={loading}
        error={error}
        columns={columns}
        rowKey="id"
        canDrag={canDrag}
        onReorder={handleReorder}
        reordering={reordering}
        empty="No questions yet"
        emptyHint={canEdit
          ? 'Use “+ Add Question” above to build the form. Respondents see the questions in the order shown here.'
          : 'This form has no questions yet.'}
      />

      <AddQuestionModal create={create} types={types} questions={orderedRows} />
      <EditQuestionModal edit={edit} types={types} questions={orderedRows} />
      <ViewQuestionModal
        row={viewing}
        questions={orderedRows}
        onClose={() => setViewing(null)}
      />
      <OptionsModal
        question={optionsFor}
        canEdit={canEdit}
        onClose={() => setOptionsFor(null)}
        // The row's ``option_count`` lives in the questions list, so the
        // list has to be re-read when an option is added or removed —
        // otherwise the count in the table lies until the next navigation.
        onChanged={reload}
      />

      <UniConfirmModal state={del} title="Delete question"
                       confirmLabel="Delete question">
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
          Delete <strong>{del.row?.text}</strong>?
          {' '}Its answer options go with it, and this can't be undone.
        </p>
        {/* Deleting a question that has already been answered orphans those
            answers.  The list payload has no per-question answer count, so
            the warning is worded for the form as a whole rather than
            claiming a number it can't know. */}
        {form?.response_count > 0 && (
          <div className="uni-note uni-note--warn" style={{ marginTop: 12 }}>
            <span aria-hidden="true">⚠️</span>
            <span>
              This form already has <strong>{fmtCount(form.response_count)}</strong>
              {' '}response{form.response_count === 1 ? '' : 's'}. Removing a
              question drops the answers people gave to it, and they won't
              appear in exports any more. Consider duplicating the form
              instead if you need to keep the old results intact.
            </span>
          </div>
        )}
      </UniConfirmModal>
    </UniPageShell>
  );
}
